// Pure translation helpers. No React, no I/O — everything here is a plain
// function so it can be unit-tested directly.

const FALLBACK_LANGUAGE = 'sl';

/**
 * Resolve a dotted path against a nested dictionary.
 * Returns undefined for a missing path, a non-string leaf, or an empty
 * string — an empty override must fall through to the bundled default.
 */
export function lookup(dict, key) {
  if (!dict || typeof dict !== 'object') return undefined;

  let value = dict;
  for (const part of String(key).split('.')) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part];
    } else {
      return undefined;
    }
  }

  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Replace {{token}} placeholders. Unknown tokens are left literal so a
 *  missing variable is visible rather than silently blank. */
export function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (match, name) =>
    name in vars ? String(vars[name]) : match
  );
}

/** Every dotted leaf path in a nested dictionary, sorted. */
export function collectKeys(obj, prefix = '') {
  const keys = [];

  for (const [key, value] of Object.entries(obj || {})) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      keys.push(...collectKeys(value, path));
    } else {
      keys.push(path);
    }
  }

  return keys.sort();
}

/**
 * Build a `t(key, vars)` function.
 * Resolution order: override → default for the current language →
 * Slovenian default → the key itself.
 */
export function createT({
  translations,
  overrides,
  language,
  fallbackLanguage = FALLBACK_LANGUAGE,
}) {
  return (key, vars) => {
    const raw =
      lookup(overrides && overrides[language], key) ??
      lookup(translations && translations[language], key) ??
      lookup(translations && translations[fallbackLanguage], key) ??
      key;

    return interpolate(raw, vars);
  };
}

/**
 * Turn a server error payload into a display string.
 * Prefers a translated `errors.<CODE>` entry, falls back to the server's own
 * message so an unmapped code still shows something useful rather than a raw
 * code. This is why the code map does not need to be exhaustive.
 */
export function translateError(payload, t) {
  const { code, error } = payload || {};

  if (code) {
    const translated = t(`errors.${code}`);
    if (translated !== `errors.${code}`) return translated;
  }

  return error || t('errors.SERVER_ERROR');
}
